import React, { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useAlerts } from '@theneatcompany/nui'

import { useSignUp } from '@/components/Providers/SignUpProvider'
import Yup from '@/utils/yup'
import Form from '@/components/forms/Form'
import { Column, Box } from '@/components/layout'
import InputField from '@/components/forms/InputField'
import GoogleLoginButton from '@/components/Pages/SignUp/GoogleLoginButton'
import { Link } from '@/components/common'
import useResponsiveDimensions from '@/hooks/useResponsiveDimensions'
import { buildTrialDisclosure } from '@/utils/trialDisclosure'
import Spinner from '@/components/common/FullScreenSpinner'
import WebAppLink from '@/components/common/WebAppLink'

import FormBox from './FormBox'
import SignUpHeading from './SignUpHeading'
import styles from './SignUp.module.scss'

const DISALLOWED_EMAIL_DOMAINS = ['@emailboxi.live']

const getMessageId = id => `pages.signUp.form.authenticate.${id}`

const UserLookup = () => {
  const {
    saveStepAuthenticate,
    plan,
    user,
  } = useSignUp()

  const [isLoading, setIsLoading] = useState(false)

  const alerts = useAlerts()
  const schema = Yup.object({
    email: Yup.string().trim().lowercase().email('Invalid email format')
      .required('An email is required to continue.'),
  })

  const formRef = useRef(null)
  const formDimensions = useResponsiveDimensions(formRef)

  const trialDisclosure = buildTrialDisclosure(plan)

  // The overlay this raises blocks the whole page, so it is lowered in a
  // `finally` -- a rejected submit, or the guard below turning one away, used
  // to leave it up with no way back to the form.
  const saveData = async (model) => {
    setIsLoading(true)
    try {
      if (!model.email || DISALLOWED_EMAIL_DOMAINS.some(d => model.email.includes(d))) {
        alerts.error('Please provide an email or login with Google')
        return
      }

      await saveStepAuthenticate(model)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async (response = {}) => {
    setIsLoading(true)
    try {
      await saveStepAuthenticate({}, response?.credential)
    } finally {
      setIsLoading(false)
    }
  }

  // The layout and the side panel are held by AccountSetupFrame, one level up,
  // so that they survive the move to the profile step.
  return (
    <>
      <div className={styles.aboveFormSection}>
        <SignUpHeading plan={plan} />
        {trialDisclosure && (
          <p className={styles.trialDisclosure}>
            {trialDisclosure.requirement}<br />{trialDisclosure.billing}
          </p>
        )}
      </div>
      <FormBox shouldCalculateMargin={false} withPricingBar={false}>
        <h3 className={`text-center ${styles.cardHeading}`}>
          <FormattedMessage id={getMessageId('accountHeading')} />
        </h3>
        <Form
          ref={formRef}
          initialValues={{ email: user?.email ?? '' }}
          onSubmit={saveData}
          schema={schema}
        >
          <Column alignCenter>
            <GoogleLoginButton
              onSuccess={handleGoogleLogin}
              width={Math.ceil(formDimensions.width)}
            />
          </Column>
          <div className={styles.orGoogle}>
            <span><FormattedMessage id={getMessageId('separator')} /></span>
          </div>
          <Column>
            <InputField
              data-testid="signup-email-input-field"
              helperProps={{
                'data-testid': 'email-validation-error',
              }}
              label="Email address"
              leftIconProps={{ name: 'mail', fill: 'gray' }}
              name="email"
              placeholder="you@company.com"
              size="large"
            />
          </Column>
          <Form.Action
            className={styles.submit}
            data-testid="signup-get-started-cta"
            messageId={getMessageId('nextButton')}
            size="large"
            type="submit"
          />
        </Form>
        <p className="text-center">
          Already have an account?{' '}
          <WebAppLink
            className={styles.noUnderline}
            eventTrackingProperties={{ location: 'Sign Up Authenticate' }}
            messageId="cta.login"
          />
        </p>
      </FormBox>
      <Box center>
        <Link
          className={styles.securityLink}
          target="_blank"
          to="/policies/privacy/"
        >
          Security &amp; Privacy
        </Link>
      </Box>
      {isLoading && (
        <Spinner>
          <h2>Verifying</h2>
          <p>Please wait...</p>
        </Spinner>
      )}
    </>
  )
}

export default UserLookup
